import React, { useState } from "react";
import { useDispatch } from "react-redux";
import {
	Box,
	Button,
	MenuItem,
	Select,
	TableCell,
	TableRow,
	Typography,
	Modal,
	IconButton,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { UserTableData } from "./UsersManagement";
import Grid from "@mui/material/Grid";
import { UsersGroupSelection } from "./UsersGroupSelection";
import {
	enableUser,
	disableUser,
	removeUserFromGroup,
	enableUserAdmin,
	disableUserAdmin,
} from "../actions/form";
import Chip from "@mui/material/Chip";
import Paper from "@mui/material/Paper";
import { displayOrg } from "./Login";

export const UsersManagementRow = (props: UserTableData) => {
	const { created, email, name, enabled, groups, access_level, actions } = props;

	const dispatch = useDispatch();

	const [accessLevel, setAccessLevel] = useState(access_level);
	const access_options = ["Admin", "User"];
	const [isEnabled, setEnabled] = useState(enabled);
	const [groupsModal, setGroupsModal] = useState(false);
	const openGroups = () => setGroupsModal(true);
	const closeGroups = () => setGroupsModal(false);

	const toggleUserAccessLevel = (email: string, admin: string) => {
		setAccessLevel(admin);
		if (admin === "Admin") dispatch(enableUserAdmin(email));
		else dispatch(disableUserAdmin(email));
	};

	const toggleUserApproval =
		(email: string, approve: boolean) => (event: React.MouseEvent<unknown>) => {
			setEnabled(approve);
			if (approve) dispatch(enableUser(email));
			else dispatch(disableUser(email));
		};

	const removeGroupMember = (email: string, groupName: string) => {
		if (groupName === "Admin") {
			dispatch(disableUserAdmin(email));
			setAccessLevel("User");
		}
		else dispatch(removeUserFromGroup(email, groupName));
	};

	// vie/manage groups modal
	const [openGroupsModal, setGroupsOpen] = useState(false);
	const handleGroupsModalOpen = () => setGroupsOpen(true);
	const handleGroupsModalClose = () => setGroupsOpen(false);

	function convertZuluToCST(zuluTime: string): string {
		const date = new Date(zuluTime);
		const options: Intl.DateTimeFormatOptions = {
			timeZone: 'America/Chicago',
			year: '2-digit',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: undefined,
			hour12: false
		};

		return date.toLocaleString('en-US', options);
	}

	return (
		<TableRow hover key={email} sx={{ cursor: "pointer" }}>
			<TableCell align="left">{convertZuluToCST(created)}</TableCell>
			<TableCell align="left">{email}</TableCell>
			<TableCell align="left">{name}</TableCell>
			<TableCell align="left">
				<Modal open={groupsModal} onClose={closeGroups}>
					<Paper
						elevation={6}
						sx={{
							position: "absolute",
							top: "50%",
							left: "50%",
							transform: "translate(-50%, -50%)",
							background: "white",
							transition: "all 200ms ease-in-out",
							padding: "2em",
							width: "40em",
						}}
					>
						<UsersGroupSelection
							email={email}
							displayOrg={displayOrg}
							closer={closeGroups}
						></UsersGroupSelection>
						<Button onClick={closeGroups}>Cancel</Button>
					</Paper>
				</Modal>
				<Button variant="contained" color="primary" onClick={handleGroupsModalOpen} sx={{ textTransform: "none" }}>View/Manage</Button>
				<Modal open={openGroupsModal} onClose={handleGroupsModalClose} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
					<Box sx={{ width: 400, maxHeight: 500, overflowY: "auto", backgroundColor: "white", p: 3, borderRadius: 2, position: "relative" }}>
						<IconButton onClick={handleGroupsModalClose} sx={{ position: "absolute", top: 8, right: 8 }}>
							<CloseIcon />
						</IconButton>

						<Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5, mb: 2 }}>
							<Typography component="span" noWrap>View/Manage User Groups </Typography>
							<Chip
								label={groups.includes('Internal') ? groups.length - 1 : groups.length}
								size="small"
								sx={{
									fontWeight: "bold"
								}}
							/>
						</Box>

						<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxHeight: 300, overflowY: "auto" }}>
							{groups.map((o, index) =>
								o !== "Internal" ? (
									<Chip
										key={index}
										label={o}
										onDelete={() => removeGroupMember(email, o)}
									/>
								) : null
							)}
							<Chip key={"new"} label={"+"} onClick={openGroups} />
						</Box>
					</Box>
				</Modal>
			</TableCell>
			<TableCell align="left">
				<Select
					disabled={!isEnabled}
					sx={{
						mt: "10px",
						maxHeight: {
							xs: 15,
							sm: 30,
						},
					}}
					fullWidth
					defaultValue={access_level}
					value={accessLevel}
					onChange={(e) => {
						toggleUserAccessLevel(email, e.target.value);
					}}
				>
					{access_options.map((o, index) => {
						return (
							<MenuItem key={index} value={o}>
								{o}
							</MenuItem>
						);
					})}
				</Select>
			</TableCell>
			<TableCell align="left">{isEnabled ? "Approved" : "Denied"}</TableCell>
			<TableCell align="left">
				{isEnabled ? (
					<Grid
						container
						rowSpacing={3}
						columnSpacing={{ xs: 1, sm: 2, md: 3 }}
					>
						<Button
							variant="contained"
							color="error"
							onClick={toggleUserApproval(email, false)}
						>
							Disable
						</Button>
					</Grid>
				) : (
					<Button
						variant="contained"
						color="success"
						onClick={toggleUserApproval(email, true)}
					>
						Enable
					</Button>
				)}
			</TableCell>
		</TableRow>
	);
};